import * as React from 'react'
import TextField from '@material-ui/core/TextField'

import { apiConsumer, IApiConsumer } from '../../data/ApiProvider'
import { IApiConsumerArray } from '../../data/actions.interface'
import { IReportSetting, IReportSettingsDto } from '../../data/admin/settings.interface'
import {
    getReportSettings,
    resetReportSetting,
    updateReportSettings,
} from '../../data/admin/settings.actions'
import AButton from '../../components/atoms/AButton'
import AButtonGroup from '../../components/atoms/AButtonGroup'
import AFormGroup from '../../components/atoms/AFormGroup'
import { AText } from '../../components/atoms'
import styled from '../../styledComponents'

interface IReportSettingsProps extends IApiConsumer {
    settings: IApiConsumerArray<IReportSetting>
}

interface IReportSettingsState {
    values: IReportSettingsDto
    loaded: boolean
    saving: boolean
    message: string | null
    error: string | null
}

const SettingsPanel = styled.div`
    background: #ffffff;
    border-radius: 6px;
    padding: 24px;
`

const FieldFooter = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-top: 0.25rem;
`

const Feedback = styled.p`
    margin-top: 1rem;

    &.is-error {
        color: ${props => props.theme.colorActiveError};
    }
`

class ReportSettings extends React.Component<IReportSettingsProps, IReportSettingsState> {
    state: IReportSettingsState = {
        values: {},
        loaded: false,
        saving: false,
        message: null,
        error: null,
    }

    componentDidMount() {
        this.seedValues()
    }

    componentDidUpdate() {
        this.seedValues()
    }

    /**
     * The form is only seeded once per load, otherwise a re-render while the admin
     * is typing would throw away their edits. Seeding runs on mount as well, because
     * the settings may already be cached in the provider when this page is revisited.
     */
    seedValues = () => {
        const { settings } = this.props

        if (this.state.loaded || settings.status !== 'success') {
            return
        }

        this.setState({
            loaded: true,
            values: settings.response.reduce(
                (agg: IReportSettingsDto, setting: IReportSetting) => ({
                    ...agg,
                    [setting.key]: setting.value || '',
                }),
                {},
            ),
        })
    }

    handleChange = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value

        this.setState(prevState => ({
            values: { ...prevState.values, [key]: value },
            message: null,
            error: null,
        }))
    }

    handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        this.setState({ saving: true, message: null, error: null })

        return this.props
            .dispatch(updateReportSettings(this.state.values))
            .then(() => {
                this.setState({ saving: false, loaded: false, message: 'Report settings saved.' })
            })
            .catch((res: any) => {
                this.setState({
                    saving: false,
                    error: this.readError(res),
                })
            })
    }

    handleReset = (key: string) => {
        this.setState({ saving: true, message: null, error: null })

        return this.props
            .dispatch(resetReportSetting(key))
            .then(() => {
                this.setState({
                    saving: false,
                    loaded: false,
                    message: `${key} now uses the environment value again.`,
                })
            })
            .catch((res: any) => {
                this.setState({ saving: false, error: this.readError(res) })
            })
    }

    readError = (res: any) => {
        if (res && res.data && res.data.message) {
            return typeof res.data.message === 'string'
                ? res.data.message
                : 'Please check the values and try again.'
        }

        return 'Something went wrong while saving the report settings.'
    }

    renderHelperText = (setting: IReportSetting) => {
        if (setting.source === 'database') {
            return setting.envValue
                ? `Set here. Environment value: ${setting.envValue}`
                : 'Set here. No environment value configured.'
        }

        return setting.envValue
            ? 'Using the value from the deployment environment.'
            : 'Not configured here or in the deployment environment.'
    }

    render() {
        const { settings } = this.props

        if (settings.status !== 'success') {
            return <SettingsPanel>Loading report settings..</SettingsPanel>
        }

        return (
            <SettingsPanel>
                <AText>
                    These values apply to every report in this deployment. Leave a field empty to
                    fall back to the value configured in the deployment environment.
                </AText>

                <form onSubmit={this.handleSubmit}>
                    {settings.response.map((setting: IReportSetting) => (
                        <AFormGroup key={setting.key} small>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                id={setting.key}
                                name={setting.key}
                                label={setting.label}
                                type="text"
                                value={this.state.values[setting.key] || ''}
                                onChange={this.handleChange(setting.key)}
                                helperText={this.renderHelperText(setting)}
                            />
                            <FieldFooter>
                                <AText small>
                                    {setting.source === 'database'
                                        ? 'Overridden from the admin UI'
                                        : 'From the deployment environment'}
                                </AText>
                                {setting.source === 'database' && (
                                    <AButton
                                        color="error"
                                        outline
                                        size="small"
                                        disabled={this.state.saving}
                                        onClick={() => this.handleReset(setting.key)}
                                    >
                                        Reset to environment
                                    </AButton>
                                )}
                            </FieldFooter>
                        </AFormGroup>
                    ))}

                    <AButtonGroup>
                        <AButton type="submit" color="primary" disabled={this.state.saving}>
                            {this.state.saving ? 'Saving..' : 'Save report settings'}
                        </AButton>
                    </AButtonGroup>
                </form>

                {this.state.message && <Feedback>{this.state.message}</Feedback>}
                {this.state.error && <Feedback className="is-error">{this.state.error}</Feedback>}
            </SettingsPanel>
        )
    }
}

export default apiConsumer({
    settings: () => getReportSettings(),
})(ReportSettings)
